<template>
  <i
    class="tm-visual-resize"
    :class="{
      l: $props.fromLeft,
      r: $props.fromRight,
      n: isNarrow,
      v: $props.visible || isActive,
      b: hasBorder,
      a: isActive,
    }"
    :style="styleObj"
    @mouseenter="$_toggleHover"
    @mouseleave="$_toggleHover"
    v-tm-move="moveObj"
  >
    <i class="-backlight" />
    <i class="-mark" />
  </i>
</template>

<script>
import _ from 'lodash-extended';
import { mapState } from 'vuex';
import tmMove from '../../movable/tmMove';
import resizeVisualMixin from '../../resizable/resizeVisual';
import {
  getResizeAvailability,
  mapAvailability2Cursor,
} from '../../../api/tapi';

export default {
  name: 'tm-visual-resize',
  mixins: [resizeVisualMixin],
  directives: { tmMove },

  props: {
    fromLeft: { type: Boolean, required: false, default: false },
    fromRight: { type: Boolean, required: false, default: false },
    track: { type: Object, required: true },
    isSelected: { type: Boolean, required: true },
    visible: { type: Boolean, required: true },
  },

  data() {
    return {
      hovered: false,
      resizing: false,
    };
  },

  computed: {
    ...mapState('timemachine', {
      pxResizeW: (state) => state.cssVariables.pxResizeW,
    }),

    isActive() {
      return this.track.isResizing ? this.$data.resizing : this.$data.hovered;
    },

    hasBorder() {
      return this.$props.isSelected;
    },

    rectangle() {
      return this.$props.fromLeft
        ? _.first(this.track.rectangles)
        : _.last(this.track.rectangles);
    },

    isNarrow() {
      return this.rectangle
        ? this.rectangle.width <= 2 * this.pxResizeW
        : false;
    },

    styleObj() {
      let cursor = null;
      if (this.isActive) {
        // checking for cursor only if isActive to minimize load while rendering bunch of assets
        const resizeWindow = this.track.pipa.getSceneResizeTimeWindow({
          relativeToScene: false,
        });
        const availability = getResizeAvailability(
          resizeWindow,
          this.track.pipa.scene
        );
        const cursorMap = mapAvailability2Cursor(availability);
        cursor = this.$props.fromLeft ? cursorMap.left : cursorMap.right;
      }

      return !this.rectangle
        ? null
        : {
            transform: `translate3d(${
              this.$props.fromLeft ? this.rectangle.p1.x : this.rectangle.p2.x
            }px, ${this.rectangle.p1.y}px, 0)`,
            ...(cursor ? { cursor } : null),
          };
    },
  },

  methods: {
    $_toggleHover(e) {
      this.$data.hovered = e.type === 'mouseenter';
      this.$emit('hovered', this.$data.hovered, this.rectangle);
    },
  },
};
</script>

<style lang="scss">
.tm-visual > .tm-visual-resize {
  --mark-colour: #fff;
  --mark-border: 0px;
  --backlight-visibility: hidden;

  position: absolute;
  top: var(--tm-pxRowBlankTop);
  z-index: var(--tm-zVisualResize);
  width: var(--tm-pxResizeW);
  height: var(--tm-pxAssetHeight);
  overflow: hidden;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s linear;

  &.v {
    visibility: visible;
    opacity: 1;
  }
  &.a {
    --mark-colour: #{$color-primary};
    --backlight-visibility: visible;
  }
  &.b {
    --mark-border: var(--tm-pxVisualSpanBorder);
  }

  > .-backlight {
    position: absolute;
    visibility: var(--backlight-visibility);
  }

  > .-mark {
    position: absolute;
    width: 5px;
    height: 20px;
    top: 50%;
    transform: translateY(-50%);
    border-right: 2px solid var(--mark-colour);
    border-left: 2px solid var(--mark-colour);
    box-shadow: 0 2px 4px $color-black-l;
  }

  &.l {
    left: 0;
    > .-backlight {
      top: var(--mark-border);
      right: 0;
      bottom: var(--mark-border);
      left: var(--mark-border);
      background-image: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.3) 0%,
        rgba(0, 0, 0, 0) 100%
      );
    }
    > .-mark {
      left: 8px;
    }
    &.n {
      left: -8px;
      width: 8px;

      > .-backlight {
        display: none;
      }
      > .-mark {
        left: 0;
        width: 8px;
        height: 8px;
        background-color: var(--mark-colour);
        border-radius: 50%;
        border: 1px solid rgba(0, 0, 0, 0.3);
        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
      }
    }
  }

  &.r {
    right: 0;
    > .-backlight {
      top: var(--mark-border);
      right: var(--mark-border);
      bottom: var(--mark-border);
      left: 0;
      background-image: linear-gradient(
        270deg,
        rgba(0, 0, 0, 0.3) 0%,
        rgba(0, 0, 0, 0) 100%
      );
    }
    > .-mark {
      right: 8px;
    }
    &.n {
      right: -8px;
      width: 8px;

      > .-backlight {
        display: none;
      }
      > .-mark {
        right: 0;
        width: 8px;
        height: 8px;
        background-color: var(--mark-colour);
        border-radius: 50%;
        border: 1px solid rgba(0, 0, 0, 0.3);
        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
      }
    }
  }
}
</style>
